import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import ProgressBar from 'pages/HealthCenter/ReportV1/BrainFunctionalConnectivity/Elements/components/ProgressBar'
import TitlePosition from 'pages/HealthCenter/ReportV1/BrainFunctionalConnectivity/Elements/components/TitlePosition'
import DescriptionPosition from 'pages/HealthCenter/ReportV1/BrainFunctionalConnectivity/Elements/components/DescriptionPosition'
import ProcessArrow from 'pages/HealthCenter/ReportV1/BrainFunctionalConnectivity/Elements/components/ProcessArrow'
import useHealthCenterStyles from 'pages/HealthCenter/ReportV1/BrainFunctionalConnectivity/Elements/PercentBarCardV3/style'
import {HEALTH_POINT} from 'constants/HealthConstant'

interface Props {
  headerName: string
  percentage: number
  index: number
  top: string
  middle: string
  bottom: string
  percentile?: {
    low: number
    high: number
  }
  pointUnit: string
}

function PercentBarCardV3({
  headerName,
  percentage,
  index,
  top,
  middle,
  bottom,
  percentile = {low: HEALTH_POINT.low, high: HEALTH_POINT.high},
  pointUnit,
}: Props) {
  const classes = useHealthCenterStyles()
  const {t} = useTranslation()
  const listPosition = {
    TOP_LEFT: 1,
    TOP_RIGHT: 2,
    BOT_CENTER: 3,
  }
  const contentTitlePosition = [
    {
      id: 0,
      text: top,
    },
    {
      id: 1,
      text: middle,
    },
    {
      id: 0,
      text: bottom,
    },
  ]

  const getTextInRound = useMemo(() => {
    if (Math.floor(percentage) < percentile.low) {
      return index === 3 ? top : bottom
    }
    if (Math.floor(percentage) > percentile.high) {
      return index === 3 ? bottom : top
    }
    return middle
  }, [percentage, t, percentile])

  const getColorInRound = useMemo(() => {
    if (Math.floor(percentage) < percentile.low) {
      return '#FF0404'
    }
    if (Math.floor(percentage) > percentile.high) {
      return '#008BC9'
    }
    return '#B4B4B4'
  }, [percentage, t, percentile])

  const checkPosition = useMemo(() => {
    if (index === listPosition.TOP_LEFT) {
      return classes.positionTopLet
    }
    if (index === listPosition.TOP_RIGHT) {
      return classes.positionTopRight
    }

    return classes.positionBotLeft
  }, [index])

  const checkTitlePosition = useMemo(() => {
    if (index === listPosition.TOP_LEFT) {
      return classes.titlePositionLeft
    }
    if (index === listPosition.TOP_RIGHT) {
      return classes.titlePositionRight
    }

    return ''
  }, [index])

  const checkPercentBarCard = useMemo(() => {
    if (index === listPosition.TOP_LEFT) {
      return classes.percentBarCardLeft
    }
    if (index === listPosition.TOP_RIGHT) {
      return classes.percentBarCardRight
    }

    return ''
  }, [index])

  const checkProcessBar = useMemo(() => {
    if (index === listPosition.TOP_LEFT) {
      return classes.progressBarLeft
    }
    if (index === listPosition.TOP_RIGHT) {
      return classes.progressBarRight
    }

    return ''
  }, [index])

  const checkProcessArrow = useMemo(() => {
    if (index === listPosition.TOP_LEFT) {
      return classes.progressArrowLeft
    }
    if (index === listPosition.TOP_RIGHT) {
      return classes.progressArrowRight
    }

    return ''
  }, [index])

  const getBorderlineDot = useMemo(() => {
    if (Math.floor(percentage) < percentile.low) {
      return '1px dotted #FF0404'
    }
    if (Math.floor(percentage) > percentile.high) {
      return '1px dotted #008BC9'
    }
    return '#B4B4B4 dotted 1px '
  }, [percentage, t, percentile])

  const getBackGroundColor = useMemo(() => {
    if (Math.floor(percentage) < percentile.low) {
      return '#EE3D3B solid 2px'
    }
    if (Math.floor(percentage) > percentile.high) {
      return '#20A2DC solid 2px'
    }
    return ''
  }, [percentage, t, percentile])

  const getColorText = useMemo(() => {
    if (Math.floor(percentage) < percentile.low) {
      return '#FF0404'
    }
    if (Math.floor(percentage) > percentile.high) {
      return '#008BC9'
    }
    return ''
  }, [percentage, t, percentile])

  return (
    <div className={`${checkPosition} ${classes.connectBrainRegionPart2} `}>
      <DescriptionPosition
        text={getTextInRound}
        index={index}
        getColorInRound={getColorInRound}
        headerName={headerName}
        getColorText={getColorText}
        getBorderlineDot={getBorderlineDot}
      />

      <div
        className={`${classes.percentBarCard} ${checkPercentBarCard}`}
        style={{
          border: getBackGroundColor,
        }}
      >
        <div className={classes.topScorePercent} style={{color: getColorText}}>
          <span
            className={classes.topScorePercentScore}
            style={{color: getColorText}}
          >
            {percentage}
          </span>
          {pointUnit.length > 0 && pointUnit}
        </div>
        <div
          className={
            index === 3 ? classes.percentBarV2Bot : classes.percentBarV2Top
          }
        >
          <div className={checkProcessArrow}>
            <ProcessArrow index={index} percentage={percentage} />
          </div>

          <div className={checkProcessBar}>
            <ProgressBar index={index} percentile={percentile} />
          </div>
          <div className={checkTitlePosition}>
            <TitlePosition
              contentTitlePosition={contentTitlePosition}
              index={index}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default PercentBarCardV3
